/**
 * Billing math — payments, balance owing, and document numbering.
 *
 * PURE helpers (no I/O). They build on top of the estimate engine
 * (see lib/estimate.ts): callers pass in the *live estimate total* from
 * calcEstimate; billing decides whether the customer actually owes that number
 * or a locked invoice amount. Mirrored server-side for caching / the auto-paid
 * check — keep the two in sync.
 *
 * Rules (V4 §4, V5 §6 — CC-fee model updated in 8c.2):
 *  - Once a (non-void) invoice is generated it LOCKS the taxable amount owed to
 *    its snapshot total; later estimate edits don't change what's already owed.
 *  - The CC processing fee is NON-taxable and added AFTER tax. It is counted on
 *    BOTH sides: it raises the billable total (getBillableTotal) and it is part
 *    of what the customer paid (getPaidTotal), so a fully-paid CC transaction
 *    nets to a $0.00 balance.
 */

export type PaymentType = 'deposit' | 'payment' | 'refund';

export interface PaymentRow {
  amount?: number | string | null;
  payment_type?: PaymentType | string | null;
  cc_fee_amount?: number | string | null;
}

export interface DocumentRow {
  doc_type?: string | null;
  status?: string | null;
  number?: string | null;
  generated_at?: string | null;
  snapshot?: { total?: number | string | null } | null;
}

export interface BillingLead {
  id?: string | null;
  stage?: string | null;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** The most-recent non-void invoice for a lead, or null. */
export function latestInvoice(documents: DocumentRow[]): DocumentRow | null {
  const invoices = (documents ?? [])
    .filter((d) => d.doc_type === 'invoice' && d.status !== 'void')
    .sort((a, b) => new Date(b.generated_at ?? 0).getTime() - new Date(a.generated_at ?? 0).getTime());
  return invoices[0] ?? null;
}

/** Sum of CC processing fees across all of a lead's payments. */
export function getCcFeeTotal(payments: PaymentRow[]): number {
  let sum = 0;
  for (const p of payments ?? []) sum += num(p.cc_fee_amount);
  return round2(sum);
}

/**
 * What the customer owes: the taxable base (a locked non-void invoice's snapshot
 * total, else the live estimate total) PLUS all CC processing fees charged. The
 * CC fees are added here so the billable side matches the paid side.
 */
export function getBillableTotal(
  _lead: BillingLead,
  documents: DocumentRow[],
  liveEstimateTotal: number,
  payments: PaymentRow[] = []
): number {
  const inv = latestInvoice(documents);
  const base =
    inv && inv.snapshot && inv.snapshot.total != null ? num(inv.snapshot.total) : num(liveEstimateTotal);
  return round2(base + getCcFeeTotal(payments));
}

/**
 * Total paid. A deposit/payment contributes amount + its CC fee; a refund
 * subtracts its amount. CC fees are included so they offset the billable side.
 */
export function getPaidTotal(payments: PaymentRow[]): number {
  let sum = 0;
  for (const p of payments ?? []) {
    const amt = num(p.amount);
    if (p.payment_type === 'refund') sum -= amt;
    else sum += amt + num(p.cc_fee_amount); // deposit | payment (+ CC fee)
  }
  return round2(sum);
}

/** Balance owing = billable − paid. */
export function getBalance(
  lead: BillingLead,
  documents: DocumentRow[],
  payments: PaymentRow[],
  liveEstimateTotal: number
): number {
  return round2(getBillableTotal(lead, documents, liveEstimateTotal, payments) - getPaidTotal(payments));
}

export type BalanceColor = 'green' | 'amber' | 'red' | 'neutral';

/**
 * Balance-owing colour (V4 §4):
 *  0 → green; >0 with some payment → amber;
 *  >0 with no payments AND stage in (Booked, Invoiced, Closed) → red; else neutral.
 *
 * INVOICED BELONGS IN THE RED SET, and is in fact the clearest case in it: the
 * move has happened, an invoice has been issued, and nothing has been paid.
 * Omitting it would have left the one stage that exists to mean "money is
 * outstanding" rendering its balance in neutral grey.
 */
export function balanceColor(balance: number, paidTotal: number, stage: string | null | undefined): BalanceColor {
  if (round2(balance) <= 0) return 'green';
  if (paidTotal > 0) return 'amber';
  if (stage === 'Booked' || stage === 'Invoiced' || stage === 'Closed') return 'red';
  return 'neutral';
}

// Document numbering lived here as an unused mirror of the server's copy. E4b
// removed it: the SERVER is the only thing that ever assigns a document number
// (it needs the sibling documents to compute the ordinal), so a client-side
// duplicate could only ever drift. Display formatting lives in lib/jobNumber.ts.
